/**
 * The hound. The only entity in the game that will reliably kill you.
 *
 * It is blind — `sightRange` is 0 and no sight signal is ever read here. It
 * knows the world entirely through `player.noise` and one-off noise events, so
 * the counterplay is real and simple: stop moving. Crouching still drops the
 * player's noise to zero and the hound loses them completely.
 *
 * It also always growls before it commits. A player who is paying attention
 * gets a second and a half of warning, which is the difference between a horror
 * game and a gotcha — and the first hound of a run gets more than double that,
 * because a player who has never met one is not paying attention to anything
 * yet. See `ENTITY_TUNING.hound.firstGrowlLead`.
 *
 * The wind-up growl repeats for the whole lead rather than firing once. That is
 * deliberate and it is the difference between a warning and a noise: a single
 * one-shot down a reverberant corridor cannot be placed, so "stop and listen"
 * has nothing to act on. A pulse train can be walked away from.
 */

import { TUNING } from '../../core/config';
import { clamp01, dist2 } from '../../core/math';
import {
  driveAwareness,
  faceToward,
  holdStill,
  moveTo,
  setMode,
  sweepStep,
  takeHeardNoise,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';
import { hearSignal } from '../perception';

const T = ENTITY_TUNING.hound;

/** Point the hound's memory at a world position, allocating it once. */
function remember(a: Agent, x: number, z: number): void {
  const s = a.state;
  if (!s.lastKnown) s.lastKnown = { x, z };
  else {
    s.lastKnown.x = x;
    s.lastKnown.z = z;
  }
}

function wander(a: Agent, w: EntityWorld): void {
  setMode(a, w, 'wander');
  // Sweeping rather than drifting, and it is the one change this file has had.
  //
  // A hound is placed up to thirty-eight metres away, and at a walk the player
  // is audible to it from about twenty. So it used to spend the encounter
  // wandering in circles around wherever it appeared, and "only the dog comes
  // to you, and it's not reliable" was a fair description: measured over 32
  // spawns at 34 m against a walking player, 9 ever reached them, the median of
  // those took 36 seconds and the ninetieth percentile never arrived at all.
  // With the sweep it is 26 of 32, median 23 s, p90 35 s.
  //
  // It costs the counterplay nothing, which is the only reason it is allowed
  // here. `sweepStep` is gated on the player's noise against the same
  // `noiseThreshold` the hound's own ears use — a crouched player emits 0.08
  // against a threshold of 0.22 — so a hound cannot be swept toward someone who
  // is standing still. The control run bears that out: 0 of 32 against a
  // crouched player, before and after, unchanged.
  sweepStep(a, w, a.def.walkSpeed);

  a.timer -= w.dt;
  if (a.timer <= 0) {
    a.timer = a.rng.range(T.idleInterval[0], T.idleInterval[1]);
    // A grumble from somewhere in the dark with nothing behind it. It is not
    // hunting; it just wants the player to know the level has teeth.
    w.audio.play('hound-growl', { pos: a.state.pos, gain: 0.32, rate: 0.85 });
  }

  if (a.state.awareness > 0.22 && a.state.lastKnown) setMode(a, w, 'investigate');
}

function investigate(a: Agent, w: EntityWorld): void {
  const s = a.state;
  if (!s.lastKnown) {
    setMode(a, w, 'wander');
    return;
  }

  if (s.awareness >= T.commitAwareness) {
    // The first of the wind-up growls goes out on the transition, not inside the
    // mode, so it lands the instant the hound commits however often the mode is
    // ticked. Low and slow: the pulses that follow climb from here.
    w.audio.play('hound-growl', { pos: s.pos, gain: 0.85, rate: 0.82 });
    a.timer = T.growlPulse;
    setMode(a, w, 'stalk');
    return;
  }

  moveTo(a, w, s.lastKnown, a.def.walkSpeed * 1.55);

  const arrived = dist2(s.pos, s.lastKnown) < 1.6 * 1.6;
  if (arrived) {
    // Nothing here. Cast around for a moment before giving up.
    holdStill(a);
    a.desiredYaw += w.dt * 1.4;
  }
  if (a.searchTimer <= 0 || (arrived && s.awareness < 0.04)) {
    s.lastKnown = null;
    s.awareness = 0;
    setMode(a, w, 'wander');
  }
}

/** The warning. It stops, growls, and only then decides whether to run. */
function windUp(a: Agent, w: EntityWorld): void {
  const s = a.state;
  // The first hound a player ever meets is the one that has to teach the rule,
  // so it takes its time about committing. Everything after it is at full speed.
  const lead = a.firstOfRun ? T.firstGrowlLead : T.growlLead;

  // Creeping, not charging. It is working out where the sound came from.
  if (s.lastKnown) moveTo(a, w, s.lastKnown, a.def.walkSpeed * 0.55);
  else holdStill(a);

  // Keep growling for the whole lead, climbing in pitch and volume as it runs
  // out. Repetition is what makes the source locatable; the climb is what makes
  // it read as a countdown rather than as scenery.
  a.timer -= w.dt;
  if (a.timer <= 0) {
    a.timer = T.growlPulse;
    const t = clamp01(s.modeTime / lead);
    w.audio.play('hound-growl', { pos: s.pos, gain: 0.85 + 0.15 * t, rate: 0.82 + 0.26 * t });
  }

  if (s.modeTime < lead) return;

  if (s.awareness >= T.commitAwareness * 0.85 && s.lastKnown) {
    w.audio.play('hound-scream', { pos: s.pos, gain: 1 });
    a.timer = 0;
    setMode(a, w, 'chase');
  } else {
    // It has lost the thread. A short deflating grumble as it goes back to
    // casting about: a player who did the counter-intuitive thing and stood
    // still has to be able to hear that it worked, or the correct answer is
    // indistinguishable from luck and they will not do it again.
    w.audio.play('hound-growl', { pos: s.pos, gain: 0.5, rate: 0.7 });
    // It has just made a noise; do not let `wander` follow it with another.
    a.timer = a.rng.range(T.idleInterval[0], T.idleInterval[1]);
    setMode(a, w, 'investigate');
  }
}

function chase(a: Agent, w: EntityWorld): void {
  const s = a.state;
  const target = s.lastKnown;
  if (!target) {
    setMode(a, w, 'investigate');
    return;
  }

  moveTo(a, w, target, a.def.chaseSpeed);

  a.timer -= w.dt;
  if (a.timer <= 0) {
    a.timer = T.stepInterval;
    w.audio.play('hound-step', { pos: s.pos, gain: 0.8, rate: 0.95 + a.rng.next() * 0.12 });
  }

  if (s.distToPlayer <= a.def.attackRange) {
    setMode(a, w, 'attack');
    return;
  }

  // It will not stop mid-lunge, but once the noise is gone it overshoots to the
  // last sound it had and then loses the thread entirely.
  if (s.modeTime > T.chaseCommit && s.awareness < T.dropAwareness) {
    if (dist2(s.pos, target) < 2.5 * 2.5) {
      s.lastKnown = null;
      s.awareness = 0;
      setMode(a, w, 'wander');
    } else {
      setMode(a, w, 'investigate');
    }
  }
}

function strike(a: Agent, w: EntityWorld): void {
  const s = a.state;
  faceToward(a, w.player.pos);
  holdStill(a);

  if (tryAttack(a, w)) {
    w.audio.play('hound-scream', { pos: s.pos, gain: 0.9, rate: 1.12 });
    // Contact is its own noise cue; right after a hit it knows exactly where you are.
    s.awareness = 1;
    remember(a, w.player.pos.x, w.player.pos.z);
  }

  if (s.distToPlayer > a.def.attackRange * 1.7) setMode(a, w, 'chase');
}

export const houndBehavior: Behavior = {
  kind: 'hound',

  spawn(a) {
    a.timer = a.rng.range(T.idleInterval[0], T.idleInterval[1]);
    a.turnRate = 5.5;
  },

  think(a, w) {
    const s = a.state;

    driveAwareness(a, w, hearSignal(a.def, s, w.grid, w.player), T.hearGain, T.forget);

    // A single loud event hands it a position even through a wall.
    const spot = takeHeardNoise(a);
    if (spot) {
      remember(a, spot.x, spot.z);
      s.awareness = Math.min(1, s.awareness + a.heardLoudness * 0.55);
      a.searchTimer = TUNING.entities.searchDuration;
    }

    switch (s.mode) {
      case 'investigate':
        investigate(a, w);
        break;
      case 'stalk':
        windUp(a, w);
        break;
      case 'chase':
        chase(a, w);
        break;
      case 'attack':
        strike(a, w);
        break;
      default:
        wander(a, w);
        break;
    }
  },
};
